import { useEffect, useRef, useState } from "react";
import { Bell, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * In-app notification bell — polls /api/inbox every 60s.
 * Renders a badge with the unread count and a dropdown with recent items.
 * Fires a sonner toast on the first render pass that reveals a NEW unread item.
 *
 * Iter 59 — used by both TrainerLayout and ClientLayout.
 */
export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenIdsRef = useRef(new Set());
  const bootstrappedRef = useRef(false);

  const fetchInbox = async () => {
    try {
      const { data } = await api.get("/inbox?limit=20");
      const nextItems = data?.items || [];
      const nextUnread = data?.unread_count || 0;
      // Detect brand-new arrivals AFTER the initial load, then toast each.
      if (bootstrappedRef.current) {
        for (const it of nextItems) {
          if (!seenIdsRef.current.has(it.id) && !it.read_at) {
            toast(it.title, { description: it.body });
          }
        }
      }
      seenIdsRef.current = new Set(nextItems.map((n) => n.id));
      bootstrappedRef.current = true;
      setItems(nextItems);
      setUnread(nextUnread);
    } catch (_e) {
      // Silent — 401s during logout are expected
    }
  };

  useEffect(() => {
    fetchInbox();
    const id = setInterval(fetchInbox, 60_000);
    return () => clearInterval(id);
  }, []);

  const markRead = async (nid) => {
    try {
      await api.post(`/inbox/${nid}/read`);
      setItems((prev) => prev.map((n) => (n.id === nid ? { ...n, read_at: new Date().toISOString() } : n)));
      setUnread((c) => Math.max(0, c - 1));
    } catch (_e) {}
  };

  const markAll = async () => {
    try {
      await api.post("/inbox/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch (_e) {}
  };

  return (
    <div className="relative" data-testid="notification-bell-wrap">
      <button
        onClick={() => setOpen((v) => !v)}
        className="btn-ghost !py-2 !px-3 relative"
        data-testid="notification-bell-button"
        aria-label="Notifications"
      >
        <Bell size={16} weight="bold" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[9px] font-bold bg-emerald-500 text-zinc-950 rounded-full"
            data-testid="notification-bell-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <button
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 cursor-default"
            aria-hidden
            data-testid="notification-bell-scrim"
          />
          <div
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-[#141414] border border-white/10 shadow-2xl z-50 max-h-[70vh] overflow-hidden flex flex-col"
            data-testid="notification-bell-panel"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <div className="tracker text-zinc-400">Notifications</div>
              <div className="flex items-center gap-2">
                {unread > 0 && (
                  <button
                    onClick={markAll}
                    className="text-[10px] tracker text-brand-light hover:text-white"
                    data-testid="notification-bell-mark-all"
                  >
                    Mark all read
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white" aria-label="Close">
                  <X size={14} weight="bold" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {items.length === 0 ? (
                <div className="p-8 text-center text-sm text-zinc-500" data-testid="notification-bell-empty">
                  You're all caught up.
                </div>
              ) : (
                items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => (n.read_at ? null : markRead(n.id))}
                    className={`w-full text-left border-b border-white/5 px-4 py-3 hover:bg-[#1c1c1c] transition-colors ${
                      n.read_at ? "opacity-60" : ""
                    }`}
                    data-testid={`notification-item-${n.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read_at && (
                        <span className="mt-1 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" aria-hidden />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-white truncate">{n.title}</div>
                        <div className="text-xs text-zinc-400 mt-0.5 line-clamp-2">{n.body}</div>
                        <div className="text-[10px] tracker text-zinc-600 mt-1">{formatWhen(n.created_at)}</div>
                      </div>
                      {n.read_at && <CheckCircle size={14} weight="bold" className="text-zinc-600 shrink-0 mt-1" />}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString();
  } catch (_e) {
    return "";
  }
}
